/** O que comparar: que elementos do IFC entram na comparação com a
 *  nuvem.
 *
 *  Comparar o levantamento inteiro com o modelo inteiro diz pouco: as
 *  paredes de alvenaria têm reboco, as lajes têm betonilha, o terreno
 *  não está no IFC. Quem fiscaliza uma estrutura metálica quer os
 *  pilares e as vigas de aço, e só esses.
 */

export type Predefinicao = 'metalica' | 'pilares-vigas' | 'seleccao' | 'classes'

export interface OpcaoPredefinida {
  id: Predefinicao
  nome: string
  /** Classes IFC, em maiúsculas, como o motor as devolve. */
  classes: string[]
  /** Texto do material, sem distinguir maiúsculas nem acentos. Vazio,
   *  qualquer material. */
  material: string
}

/** As palavras que costumam aparecer no nome de um material de aço. Nos
 *  IFC reais o material raramente se chama "Aço": chama-se "S275",
 *  "Steel - ASTM A992", "Metal - Steel 345 MPa" (Revit), "HEB 200" no
 *  nome do perfil. Daí uma lista, separada por barras. */
export const PADRAO_ACO = 'aço|aco|steel|metal|s235|s275|s355|s460|astm a|hea|heb|ipe|upn|shs|rhs|chs'

export const PREDEFINICOES: OpcaoPredefinida[] = [
  {
    id: 'metalica',
    nome: 'Estrutura metálica (pilares, vigas e barras de aço)',
    classes: ['IFCCOLUMN', 'IFCBEAM', 'IFCMEMBER'],
    material: PADRAO_ACO,
  },
  {
    id: 'pilares-vigas',
    nome: 'Pilares e vigas, de qualquer material',
    classes: ['IFCCOLUMN', 'IFCBEAM'],
    material: '',
  },
  { id: 'seleccao', nome: 'Os elementos seleccionados', classes: [], material: '' },
  { id: 'classes', nome: 'Escolher classes', classes: [], material: '' },
]

/** Tirar acentos e passar a minúsculas, para "Aço" e "ACO" e "aço"
 *  serem o mesmo. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

/** O material corresponde ao padrão? O padrão são alternativas
 *  separadas por "|", procuradas como texto e não como expressão
 *  regular: quem escreve "S275 (EN)" não quer um erro de sintaxe. */
export function materialCorresponde(material: string | null | undefined, padrao: string): boolean {
  const alternativas = padrao
    .split('|')
    .map((a) => normalizar(a.trim()))
    .filter(Boolean)
  if (alternativas.length === 0) return true
  if (!material) return false
  const m = normalizar(material)
  return alternativas.some((a) => m.includes(a))
}

/** "IFCCOLUMN" para "IfcColumn", para se ler na lista. */
export function nomeDaClasse(classe: string): string {
  const conhecidas: Record<string, string> = {
    IFCCOLUMN: 'IfcColumn',
    IFCBEAM: 'IfcBeam',
    IFCMEMBER: 'IfcMember',
    IFCPLATE: 'IfcPlate',
    IFCWALL: 'IfcWall',
    IFCWALLSTANDARDCASE: 'IfcWallStandardCase',
    IFCSLAB: 'IfcSlab',
    IFCFOOTING: 'IfcFooting',
    IFCSTAIR: 'IfcStair',
    IFCSTAIRFLIGHT: 'IfcStairFlight',
    IFCRAILING: 'IfcRailing',
    IFCROOF: 'IfcRoof',
    IFCDOOR: 'IfcDoor',
    IFCWINDOW: 'IfcWindow',
    IFCCOVERING: 'IfcCovering',
    IFCCURTAINWALL: 'IfcCurtainWall',
    IFCPILE: 'IfcPile',
  }
  const c = classe.toUpperCase()
  if (conhecidas[c]) return conhecidas[c]
  return c.startsWith('IFC') ? `Ifc${c.slice(3, 4)}${c.slice(4).toLowerCase()}` : classe
}

/** As classes que vale a pena oferecer: as que têm geometria que um
 *  laser consegue ver. Os espaços, as zonas e as aberturas são volumes
 *  de ar, e compará-los com pontos não quer dizer nada. */
export function classeComparavel(classe: string): boolean {
  const c = classe.toUpperCase()
  if (!c.startsWith('IFC')) return false
  return ![
    'IFCSPACE',
    'IFCSPATIALZONE',
    'IFCZONE',
    'IFCOPENINGELEMENT',
    'IFCOPENINGSTANDARDCASE',
    'IFCSITE',
    'IFCBUILDING',
    'IFCBUILDINGSTOREY',
    'IFCPROJECT',
    'IFCANNOTATION',
    'IFCGRID',
    'IFCVIRTUALELEMENT',
  ].includes(c)
}
